import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { SetGoalModal } from "./SetGoalModal";
import { AddProgressModal } from "./AddProgressModal";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { toast } from "sonner";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay,
  addMonths, subMonths, addDays, isToday, isSameMonth, parseISO, startOfDay,
} from "date-fns";
import {
  Target, Plus, TrendingUp, ChevronLeft, ChevronRight, Edit2, Trash2,
  CheckCircle, Scale, Ruler, Clock, Pause, Play, FileText,
  Calendar as CalendarIcon, List, Eye, ChevronDown,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend
} from "recharts";

const MEASUREMENT_INFO = {
  weight: { label: "Weight", unit: "kg", icon: Scale, color: "text-blue-500" },
  chest: { label: "Chest", unit: "cm", icon: Ruler, color: "text-purple-500" },
  waist: { label: "Waist", unit: "cm", icon: Ruler, color: "text-orange-500" },
  arm: { label: "Arm", unit: "cm", icon: Ruler, color: "text-green-500" },
  bmi: { label: "BMI", unit: "", icon: Scale, color: "text-pink-500" },
};

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const getReminderDates = (goal) => {
  if (!goal) return new Set();
  const reminderDays = goal.reminderDays || 7;
  const start = startOfDay(parseISO(goal.startDate || goal.createdAt));
  const end = goal.endDate ? startOfDay(parseISO(goal.endDate)) : null;
  const dates = new Set();
  let cursor = addDays(start, reminderDays);
  const maxDate = addDays(start, 365 * 5);
  while (cursor <= maxDate) {
    if (end && cursor > end) break;
    dates.add(format(cursor, "yyyy-MM-dd"));
    cursor = addDays(cursor, reminderDays);
  }
  return dates;
};

export function MemberProgressTab({ member }) {
  const { currentBranch, hasPermission } = useAuth();

  // ── Permission flags ────────────────────────────────────────────────
  const canViewProgress = hasPermission("view_progress");
  const canSetGoal = hasPermission("set_goal");
  const canAddProgress = hasPermission("add_progress");
  const canEditGoal = hasPermission("edit_goal");
  const canDeleteGoal = hasPermission("delete_goal");

  const [goals, setGoals] = useState([]);
  const [activeGoalDetail, setActiveGoalDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [existingEntry, setExistingEntry] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, goal: null });
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("calendar");
  const [selectedDay, setSelectedDay] = useState(new Date());

  useEffect(() => { fetchGoals(); }, [member?.id]);

  const fetchGoals = async () => {
    if (!member?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/goals?memberId=${member.id}`);
      const allGoals = res.data.goals || [];
      setGoals(allGoals);
      const active = allGoals.find(g => g.status === "Active");
      if (active) {
        setSelectedGoal(active);
        const detailRes = await api.get(`/goals/${active.id}`);
        setActiveGoalDetail(detailRes.data.goal);
      } else {
        setSelectedGoal(null);
        setActiveGoalDetail(null);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDeleteGoal = async () => {
    try {
      await api.delete(`/goals/${deleteModal.goal.id}`);
      toast.success("Goal deleted");
      setDeleteModal({ open: false, goal: null });
      fetchGoals();
    } catch { toast.error("Failed to delete goal"); }
  };

  const handleCompleteGoal = async (goalId) => {
    try {
      await api.post(`/goals/${goalId}/complete`);
      toast.success("Goal marked as completed!");
      fetchGoals();
    } catch { toast.error("Failed to complete goal"); }
  };

  const handlePauseGoal = async (goalId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Paused" ? "Active" : "Paused";
      await api.put(`/goals/${goalId}`, { status: newStatus });
      toast.success(`Goal ${newStatus === "Paused" ? "paused" : "resumed"}`);
      fetchGoals();
    } catch { toast.error("Failed to update goal status"); }
  };

  const monthStart = startOfMonth(calendarDate);
  const monthEnd = endOfMonth(calendarDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getAllProgressForDay = (date, goal) => {
    const entries = goal?.progressEntries;
    if (!entries?.length) return [];
    const dateStr = format(date, "yyyy-MM-dd");
    return entries.filter(p => {
      const d = p.recordedDate || p.date || p.createdAt || "";
      return d.slice(0, 10) === dateStr;
    });
  };

  const getProgressForDay = (date, goal) => {
    const entries = getAllProgressForDay(date, goal);
    return entries.length > 0 ? entries[0] : null;
  };

  const getDayColor = (date, goal, reminderDates) => {
    if (!goal) return "bg-transparent";
    const dateStr = format(date, "yyyy-MM-dd");
    const today = startOfDay(new Date());
    const thisDay = startOfDay(date);
    const gStart = goal ? startOfDay(parseISO(goal.startDate || goal.createdAt)) : null;
    if (gStart && thisDay < gStart) return "bg-transparent";
    if (getProgressForDay(date, goal)) return "bg-emerald-500";
    const isReminderDay = reminderDates.has(dateStr);
    if (isReminderDay) {
      if (isSameDay(thisDay, today)) return "bg-amber-500";
      if (thisDay > today) return "bg-blue-500";
      return "bg-red-500";
    }
    if (thisDay > today) return "bg-transparent";
    return "bg-muted-foreground/30";
  };

  const activeGoal = goals.find(g => g.status === "Active");
  const completedGoals = goals.filter(g => g.status === "Completed");
  const pausedGoals = goals.filter(g => g.status === "Paused");

  const calendarGoal = activeGoalDetail || activeGoal;

  const chartData = useMemo(() => {
    if (!calendarGoal?.progressEntries?.length) return [];
    return [...calendarGoal.progressEntries]
      .sort((a, b) => {
        const da = a.recordedDate || a.date || a.createdAt || "";
        const db = b.recordedDate || b.date || b.createdAt || "";
        return da.localeCompare(db);
      })
      .map(entry => {
        const rawDate = entry.recordedDate || entry.date || entry.createdAt || "";
        let formattedDate = "";
        try {
          formattedDate = rawDate ? format(parseISO(rawDate), "dd MMM") : "";
        } catch {
          formattedDate = rawDate ? rawDate.slice(5, 10) : "";
        }
        const dp = { date: formattedDate };
        (entry.measurements || []).forEach(m => {
          dp[m.metric] = m.value;
        });
        return dp;
      });
  }, [calendarGoal?.progressEntries]);

  const metrics = useMemo(() => {
    return calendarGoal?.measurements?.map(m => m.metric) || [];
  }, [calendarGoal?.measurements]);

  const reminderDates = calendarGoal ? getReminderDates(calendarGoal) : new Set();
  const goalStartDay = calendarGoal
    ? startOfDay(parseISO(calendarGoal.startDate || calendarGoal.createdAt))
    : null;

  const isDayClickable = (day) => {
    if (!canAddProgress) return false;
    if (!calendarGoal || !goalStartDay) return false;
    return startOfDay(day) >= goalStartDay;
  };

  const canAddProgressForDay = (day) => {
    if (!canAddProgress) return false;
    if (!calendarGoal || !goalStartDay) return false;
    return startOfDay(day) >= goalStartDay;
  };

  const monthStats = useMemo(() => {
    const stats = { recorded: 0, missed: 0, dueToday: 0, upcoming: 0 };
    if (!calendarGoal) return stats;
    const today = startOfDay(new Date());

    daysInMonth.forEach(day => {
      const thisDay = startOfDay(day);
      if (goalStartDay && thisDay < goalStartDay) return;

      const progress = getProgressForDay(day, calendarGoal);
      if (progress) {
        stats.recorded++;
        return;
      }

      const dateStr = format(day, "yyyy-MM-dd");
      const isReminderDay = reminderDates.has(dateStr);
      if (isReminderDay) {
        if (isSameDay(thisDay, today)) {
          stats.dueToday++;
        } else if (thisDay > today) {
          stats.upcoming++;
        } else {
          stats.missed++;
        }
      }
    });
    return stats;
  }, [calendarGoal, daysInMonth, reminderDates, goalStartDay]);

  const openProgressModal = (goal, entry = null, date = null) => {
    if (!canAddProgress) return;
    setSelectedGoal(goal);
    setExistingEntry(entry);
    setSelectedDate(date);
    setShowProgressModal(true);
  };

  // ── Guard: no view_progress → show locked state ──
  if (!canViewProgress) {
    return (
      <Card className="bg-card border-border p-8 text-center">
        <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-heading mb-2">Access Restricted</h3>
        <p className="text-sm text-muted-foreground">You don't have permission to view progress data.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="member-progress-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading uppercase tracking-wide">Progress & Goals</h3>
          <p className="text-xs text-muted-foreground">Track fitness journey and achievements</p>
        </div>
        {/* Set Goal: only if canSetGoal and no active goal */}
        {canSetGoal && (
          <Button
            size="sm"
            onClick={() => { setEditingGoal(null); setShowGoalModal(true); }}
            disabled={!!activeGoal}
            className="font-heading uppercase tracking-wide text-xs"
            data-testid="set-goal-btn"
          >
            <Plus className="h-4 w-4 mr-1" />Set Goal
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
        </div>
      ) : activeGoal ? (
        <div className="space-y-6 mt-4">
          {/* Active Goal Card */}
          <Card className="bg-card border-border overflow-hidden" data-testid="active-goal-card">
            <CardHeader className="pb-3 flex flex-row items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Target className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base font-heading">{activeGoal.name}</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] font-mono">{activeGoal.goalType}</Badge>
                  <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] font-mono">ACTIVE</Badge>
                </div>
              </div>

              {/* Action buttons — each individually gated */}
              <div className="flex gap-1">
                <TooltipProvider>
                  {canAddProgress && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => openProgressModal(activeGoal, null, new Date())}>
                          <TrendingUp className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Add Progress</TooltipContent>
                    </Tooltip>
                  )}
                  {canEditGoal && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => { setEditingGoal(activeGoal); setShowGoalModal(true); }}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit Goal</TooltipContent>
                    </Tooltip>
                  )}
                  {canEditGoal && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => handlePauseGoal(activeGoal.id, activeGoal.status)}>
                          <Pause className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Pause Goal</TooltipContent>
                    </Tooltip>
                  )}
                  {canEditGoal && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-500"
                          onClick={() => handleCompleteGoal(activeGoal.id)}>
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Mark Complete</TooltipContent>
                    </Tooltip>
                  )}
                  {canDeleteGoal && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                          onClick={() => setDeleteModal({ open: true, goal: activeGoal })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete Goal</TooltipContent>
                    </Tooltip>
                  )}
                </TooltipProvider>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Progress value={activeGoal.progressPercent || 0} className="flex-1 h-3" />
                <span className="text-lg font-mono font-bold">{activeGoal.progressPercent || 0}%</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {activeGoal.measurements?.map(m => {
                  const info = MEASUREMENT_INFO[m.metric] || { label: m.metric, unit: "", icon: Scale, color: "text-primary" };
                  const Icon = info.icon;
                  const currentValue = activeGoal.latestProgress?.measurements?.find(pm => pm.metric === m.metric)?.value || m.currentValue;
                  return (
                    <div key={m.metric} className="p-3 bg-secondary/30 rounded-lg text-center">
                      <Icon className={`h-5 w-5 mx-auto mb-1 ${info.color}`} />
                      <p className="text-xs text-muted-foreground font-mono uppercase">{info.label}</p>
                      <p className="text-lg font-mono font-bold">{currentValue}</p>
                      <p className="text-[10px] text-muted-foreground">Target: <span className="text-primary">{m.targetValue}</span> {info.unit}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Reminder every {activeGoal.reminderDays} days</span>
                </div>
                {activeGoal.nextReminderDate && (
                  <Badge variant="outline" className="text-xs font-mono">Next: {activeGoal.nextReminderDate.slice(0, 10)}</Badge>
                )}
              </div>
              {/* Add Progress CTA button */}
              {canAddProgress && (
                <Button
                  className="font-heading uppercase tracking-wide"
                  onClick={() => openProgressModal(activeGoal, null, new Date())}
                  data-testid="add-progress-btn"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />Add Progress
                </Button>
              )}
            </CardContent>
          </Card>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-secondary/50 p-1">
              <TabsTrigger value="calendar" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-calendar">
                <CalendarIcon className="h-3.5 w-3.5 mr-2" />Calendar
              </TabsTrigger>
              <TabsTrigger value="history" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-history">
                <List className="h-3.5 w-3.5 mr-2" />History
              </TabsTrigger>
              <TabsTrigger value="graph" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-graph">
                <TrendingUp className="h-3.5 w-3.5 mr-2" />Graph
              </TabsTrigger>
            </TabsList>

            <TabsContent value="calendar">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
                {/* Left: Calendar Card */}
                <Card className="lg:col-span-5 bg-card border-border">
                  <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 py-2">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-sm font-heading uppercase tracking-wide leading-none">Calendar</CardTitle>
                      <div className="flex items-center gap-2 text-[10px] font-mono font-bold leading-none animate-in fade-in duration-200">
                        {monthStats.recorded > 0 && <span className="text-emerald-500">REC: {monthStats.recorded}</span>}
                        {monthStats.missed > 0 && <span className="text-red-500">MSD: {monthStats.missed}</span>}
                        {monthStats.dueToday > 0 && <span className="text-amber-500">DUE: {monthStats.dueToday}</span>}
                        {monthStats.upcoming > 0 && <span className="text-blue-500">UPC: {monthStats.upcoming}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => setCalendarDate(subMonths(calendarDate, 1))} data-testid="prev-month-btn">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium min-w-[100px] text-center">{format(calendarDate, "MMMM yyyy")}</span>
                      <Button variant="ghost" size="icon" onClick={() => setCalendarDate(addMonths(calendarDate, 1))} data-testid="next-month-btn">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-1">
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {DAYS_OF_WEEK.map(d => (
                        <div key={d.key} className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2">{d.label}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {daysInMonth.map((day, idx) => {
                        const dayOfWeek = day.getDay();
                        const isFirstDay = idx === 0;
                        const dateStr = format(day, "yyyy-MM-dd");
                        const today = isToday(day);
                        const isSelected = selectedDay && isSameDay(selectedDay, day);
                        const progress = getProgressForDay(day, calendarGoal);
                        const hasRecord = !!progress;

                        const dayColor = getDayColor(day, calendarGoal, reminderDates);
                        const clickable = isDayClickable(day);

                        return (
                          <div key={day.toString()}
                            style={{ gridColumn: isFirstDay ? dayOfWeek + 1 : undefined }}
                            onClick={() => setSelectedDay(day)}
                            className={`min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 rounded-sm border cursor-pointer transition-all ${today ? "border-primary bg-primary/5 shadow-[0_0_10px_rgba(var(--primary),0.1)]"
                              : isSelected ? "border-primary bg-primary/5"
                                : hasRecord ? "border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10"
                                  : clickable ? "border-border bg-secondary/30 hover:bg-secondary/50"
                                    : "border-border/50 bg-background hover:bg-secondary/20"
                              }`}
                            data-testid={`calendar-day-${dateStr}`}
                          >
                            <div className={`text-[10px] font-medium mb-1 flex justify-between ${today ? "text-primary" : ""}`}>
                              <span>{format(day, "d")}</span>
                              {hasRecord && (
                                <span className="text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500 text-white">✓</span>
                              )}
                            </div>

                            <div className="flex gap-1 flex-wrap">
                              {dayColor !== "bg-transparent" && (
                                <div className={`h-1.5 w-1.5 rounded-full ${dayColor === "bg-muted-foreground/30" ? "bg-muted-foreground/40" : dayColor
                                  }`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6 mt-4 pt-4 border-t border-border flex-wrap animate-in fade-in duration-200">
                      {[
                        { color: "bg-emerald-500", label: "Recorded" },
                        { color: "bg-red-500", label: "Missed" },
                        { color: "bg-amber-500", label: "Due Today" },
                        { color: "bg-blue-500", label: "Upcoming" },
                        { color: "bg-muted-foreground/40", label: "No Record" },
                      ].map(l => (
                        <div key={l.label} className="flex items-center gap-1.5">
                          <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                          <span className="text-[11px] text-muted-foreground">{l.label}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Right: Day Details Card */}
                <Card className="lg:col-span-7 bg-card border-border flex flex-col h-full min-h-[500px] lg:h-[calc(100vh-280px)]">
                  <CardHeader className="px-3 py-2 border-b border-border shrink-0">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 text-primary" />
                      <CardTitle className="text-sm font-heading uppercase tracking-wide">
                        {selectedDay ? format(selectedDay, "EEEE, MMM d, yyyy") : "Daily Details"}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-y-auto p-0 scroll-smooth">
                    {!selectedDay ? (
                      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                        <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                          <CalendarIcon className="h-6 w-6" />
                        </div>
                        <p className="text-sm font-medium">Select a day from the calendar</p>
                        <p className="text-xs mt-1">to view progress details</p>
                      </div>
                    ) : (
                      (() => {
                        const dayProgressEntries = getAllProgressForDay(selectedDay, calendarGoal);
                        const clickable = canAddProgressForDay(selectedDay);
                        if (dayProgressEntries.length === 0) {
                          return (
                            <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground ">
                              <TrendingUp className="h-10 w-10 mb-3" />
                              <p className="text-sm">No progress recorded for this day</p>
                              {clickable && (
                                <Button size="sm" className="mt-2 text-xs" onClick={() => openProgressModal(calendarGoal, null, selectedDay)}>
                                  <Plus className="h-3.5 w-3.5 mr-1" />Add Progress
                                </Button>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div className="p-1 md:p-4 space-y-4">
                            {/* Day Summary */}
                            <div className="flex items-center justify-between px-3 py-2 bg-secondary/30 rounded-xl border border-border">
                              <div className="flex flex-col gap-1.5 w-full">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-baseline gap-2">
                                    <p className="text-lg font-black font-heading leading-none">Recorded</p>
                                    <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] font-mono">
                                      ✓ {dayProgressEntries.length} {dayProgressEntries.length === 1 ? "ENTRY" : "ENTRIES"}
                                    </Badge>
                                  </div>
                                  {clickable && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs h-8"
                                      onClick={() => openProgressModal(calendarGoal, null, selectedDay)}
                                    >
                                      <Plus className="h-3.5 w-3.5 mr-1" />Add Another
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Details Containers for each progress entry */}
                            {dayProgressEntries.map((progress, idx) => (
                              <div key={progress.id || idx} className="border border-border rounded-xl bg-card overflow-hidden">
                                <div className="flex items-center justify-between gap-2 border-b border-border bg-secondary/20 p-2 px-3 text-[11px] text-muted-foreground font-medium">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <Badge variant="outline" className="text-[9px] font-mono px-1 py-0 h-4 border-primary/30 text-primary">
                                      #{idx + 1}
                                    </Badge>
                                    <span>Logged by <strong className="text-foreground">{progress.creatorName || "Staff"}</strong></span>
                                    {progress.createdAt && <span className="opacity-40">|</span>}
                                    {progress.createdAt && (
                                      <span>{new Date(progress.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                                    )}
                                  </div>
                                  {canAddProgress && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-[10px] gap-1 font-heading uppercase tracking-wider shrink-0"
                                      onClick={(e) => { e.stopPropagation(); openProgressModal(calendarGoal, progress, null); }}
                                    >
                                      <Edit2 className="h-3 w-3" /> Edit Entry
                                    </Button>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
                                  {progress.measurements?.map(m => {
                                    const info = MEASUREMENT_INFO[m.metric] || { label: m.metric, unit: "", icon: Scale, color: "text-primary" };
                                    const Icon = info.icon;
                                    return (
                                      <div key={m.metric} className="p-3 bg-secondary/30 rounded-lg text-center border border-border/40">
                                        <Icon className={`h-5 w-5 mx-auto mb-1 ${info.color}`} />
                                        <p className="text-xs text-muted-foreground font-mono uppercase">{info.label}</p>
                                        <p className="text-lg font-mono font-bold">{m.value} <span className="text-xs font-normal text-muted-foreground">{info.unit}</span></p>
                                      </div>
                                    );
                                  })}
                                </div>
                                {progress.notes && (
                                  <div className="p-4 border-t border-border/40 space-y-1">
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Notes / Comments</p>
                                    <p className="text-sm text-muted-foreground leading-normal whitespace-pre-wrap">{progress.notes}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="history">
              {calendarGoal?.progressEntries?.length > 0 ? (
                <Card className="bg-card border-border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary" />Progress History
                      </CardTitle>
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {calendarGoal.progressEntries.length} {calendarGoal.progressEntries.length === 1 ? "entry" : "entries"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                    {[...calendarGoal.progressEntries]
                      .sort((a, b) => {
                        const da = a.recordedDate || a.date || a.createdAt || "";
                        const db = b.recordedDate || b.date || b.createdAt || "";
                        return db.localeCompare(da);
                      })
                      .map((entry, i) => {
                        const rawDate = entry.recordedDate || entry.date || entry.createdAt || "";
                        let displayDate = "—";
                        try { if (rawDate) displayDate = format(parseISO(rawDate), "dd MMM yyyy"); } catch { }
                        return (
                          <div
                            key={entry.id || i}
                            className={`p-3 bg-secondary/20 rounded-lg border border-border/40 space-y-2 ${canAddProgress ? "cursor-pointer hover:bg-secondary/30 transition-colors" : ""
                              }`}
                            onClick={() => canAddProgress && openProgressModal(calendarGoal, entry, null)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-mono font-semibold">{displayDate}</p>
                                {entry.createdAt && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {new Date(entry.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-muted-foreground font-medium">by {entry.creatorName || "Staff"}</span>
                                <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] font-mono">Recorded</Badge>
                              </div>
                            </div>
                            {entry.measurements?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {entry.measurements.map(m => {
                                  const info = MEASUREMENT_INFO[m.metric] || { label: m.metric, unit: "" };
                                  return (
                                    <Badge key={m.metric} variant="outline" className="text-[10px] font-mono gap-1">
                                      <span className="text-muted-foreground">{info.label}:</span>
                                      <span className="font-bold">{m.value}</span>
                                      {info.unit && <span className="text-muted-foreground">{info.unit}</span>}
                                    </Badge>
                                  );
                                })}
                              </div>
                            )}
                            {entry.notes && (
                              <div className="flex items-start gap-1.5 pt-1 border-t border-border/30">
                                <FileText className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                                <p className="text-xs text-muted-foreground leading-relaxed">{entry.notes}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </CardContent>
                </Card>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <TrendingUp className="h-10 w-10 mb-3" />
                  <p className="text-sm">No progress history found</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="graph">
              {calendarGoal?.progressEntries?.length > 0 ? (
                <Card className="bg-card border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />Progress Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="h-[350px] pt-4" data-testid="progress-graph-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                        <XAxis dataKey="date" className="text-[10px] font-mono fill-muted-foreground" />
                        <YAxis className="text-[10px] font-mono fill-muted-foreground" />
                        <ChartTooltip 
                          contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                          labelStyle={{ color: "hsl(var(--muted-foreground))", fontSize: "11px", fontWeight: "bold" }}
                          itemStyle={{ fontSize: "12px" }}
                        />
                        <Legend className="text-xs font-mono uppercase" />
                        {metrics.map((metric, idx) => {
                          const colors = ["#2563eb", "#10b981", "#d97706", "#db2777", "#7c3aed"];
                          return (
                            <Line
                              key={metric}
                              type="monotone"
                              dataKey={metric}
                              name={metric.charAt(0).toUpperCase() + metric.slice(1)}
                              stroke={colors[idx % colors.length]}
                              activeDot={{ r: 6 }}
                              strokeWidth={2}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-card border-border p-8 text-center">
                  <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Not enough progress history to plot graph</p>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <Card className="bg-card border-border p-8 text-center">
          <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-heading mb-2">No Active Goal</h3>
          <p className="text-sm text-muted-foreground mb-4">Set a fitness goal to start tracking progress</p>
          {canSetGoal && (
            <Button onClick={() => { setEditingGoal(null); setShowGoalModal(true); }}>
              <Plus className="h-4 w-4 mr-2" />Set First Goal
            </Button>
          )}
        </Card>
      )}

      {/* Past Goals */}
      {(completedGoals.length > 0 || pausedGoals.length > 0) && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Past Goals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pausedGoals.map(goal => (
              <div key={goal.id} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{goal.name}</p>
                    <Badge className="bg-amber-500/15 text-amber-500 border-0 text-[10px]">PAUSED</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{goal.goalType} • {goal.progressPercent}% complete</p>
                </div>
                <div className="flex gap-1">
                  {canEditGoal && (
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => handlePauseGoal(goal.id, goal.status)}>
                      <Play className="h-4 w-4" />
                    </Button>
                  )}
                  {canDeleteGoal && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                      onClick={() => setDeleteModal({ open: true, goal })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {completedGoals.map(goal => (
              <div key={goal.id} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{goal.name}</p>
                    <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px]">COMPLETED</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{goal.goalType} • Completed {goal.completedAt?.slice(0, 10)}</p>
                </div>
                {canDeleteGoal && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                    onClick={() => setDeleteModal({ open: true, goal })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Modals — only mount if permitted */}
      {canSetGoal && (
        <SetGoalModal
          open={showGoalModal}
          onOpenChange={setShowGoalModal}
          member={member}
          branchId={currentBranch?.id}
          existingGoal={editingGoal}
          onSuccess={fetchGoals}
        />
      )}
      {canAddProgress && (
        <AddProgressModal
          open={showProgressModal}
          onOpenChange={setShowProgressModal}
          goal={selectedGoal}
          existingEntry={existingEntry}
          selectedDate={selectedDate}
          branchId={currentBranch?.id}
          onSuccess={() => { setShowProgressModal(false); fetchGoals(); }}
        />
      )}
      {canDeleteGoal && (
        <DeleteConfirmModal
          open={deleteModal.open}
          onOpenChange={(open) => setDeleteModal({ open, goal: open ? deleteModal.goal : null })}
          title="Delete Goal"
          description="Are you sure you want to delete this goal and all its progress entries?"
          itemName={deleteModal.goal?.name}
          onConfirm={handleDeleteGoal}
        />
      )}
    </div>
  );
}
